(() => {
  if (window.amaroHome) return;
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const instances = new Map();
  const parallaxItems = new Set();
  let scrollFrame = 0;
  const pendingColorLayouts = new Map();
  let colorFrame = 0;
  const queueColorLayout = (row, measure) => {
    pendingColorLayouts.set(row, measure);
    if (colorFrame) return;
    colorFrame = requestAnimationFrame(() => {
      colorFrame = 0;
      // Measure every row before changing any row's layout.
      const writes = Array.from(pendingColorLayouts.values(), read => read()).filter(Boolean);
      pendingColorLayouts.clear();
      writes.forEach(write => write());
    });
  };

  const paintParallax = () => {
    scrollFrame = 0;
    const viewport = window.innerHeight;
    for (const item of parallaxItems) {
      if (!item.visible && !reducedMotion.matches) continue;
      const bounds = item.element.getBoundingClientRect();
      const progress = (viewport / 2 - (bounds.top + bounds.height / 2)) / ((viewport + bounds.height) / 2);
      const strength = Math.min(100, Number(item.element.dataset.parallaxStrength) || 65);
      const mobileFactor = window.innerWidth < 750 ? 0.55 : 1;
      const shift = reducedMotion.matches ? 0 : Math.max(-1, Math.min(1, progress)) * strength * mobileFactor;
      item.media.style.transform = `translate3d(0, ${shift.toFixed(2)}px, 0)`;
    }
  };
  const scheduleParallax = () => {
    if (!scrollFrame) scrollFrame = requestAnimationFrame(paintParallax);
  };

  const mount = (root) => {
    if (instances.has(root)) return;
    const controller = new AbortController();
    const { signal } = controller;
    const cleanups = [];
    const colorRows = new Map();
    const layoutColors = (row, keepSelected = true) => queueColorLayout(row, () => {
      const state = colorRows.get(row);
      if (!state || !row.isConnected || !row.clientWidth) return;
      const size = parseFloat(getComputedStyle(state.buttons[0]).width);
      const moreSize = parseFloat(getComputedStyle(state.more).width);
      const gap = parseFloat(getComputedStyle(row).columnGap) || 0;
      const total = state.buttons.length;
      const allFit = total * size + (total - 1) * gap <= row.clientWidth;
      state.visibleCount = allFit ? total : Math.max(1, Math.floor((row.clientWidth - moreSize) / (size + gap)));
      if (allFit) state.offset = 0;
      if (keepSelected) {
        const selected = state.buttons.findIndex((button) => button.getAttribute('aria-pressed') === 'true');
        if (selected >= 0 && (selected - state.offset + total) % total >= state.visibleCount) state.offset = selected;
      }
      return () => {
        state.buttons.forEach((button, index) => {
          const position = (index - state.offset + total) % total;
          button.hidden = position >= state.visibleCount;
          button.style.order = position;
        });
        state.more.hidden = allFit;
        state.more.style.order = total;
        state.more.textContent = `+${total - state.visibleCount}`;
        state.more.setAttribute('aria-label', `Ver ${total - state.visibleCount} colores más`);
      };
    });
    root.querySelectorAll('[data-amaro-color-row]').forEach((row) => {
      const buttons = Array.from(row.querySelectorAll('[data-amaro-color]'));
      const more = row.querySelector('[data-amaro-more-colors]');
      if (!buttons.length || !more) return;
      colorRows.set(row, { buttons, more, offset: 0, visibleCount: buttons.length });
      layoutColors(row);
    });
    cleanups.push(() => colorRows.forEach((_, row) => pendingColorLayouts.delete(row)));
    if ('ResizeObserver' in window) {
      const colorResize = new ResizeObserver((entries) => entries.forEach(({ target }) => layoutColors(target)));
      colorRows.forEach((_, row) => colorResize.observe(row));
      cleanups.push(() => colorResize.disconnect());
    } else {
      window.addEventListener('resize', () => colorRows.forEach((_, row) => layoutColors(row)), { signal });
    }
    root.addEventListener('click', (event) => {
      const more = event.target.closest('[data-amaro-more-colors]');
      if (more && root.contains(more)) {
        const row = more.closest('[data-amaro-color-row]');
        const state = colorRows.get(row);
        if (state) {
          state.offset = (state.offset + state.visibleCount) % state.buttons.length;
          layoutColors(row, false);
        }
        return;
      }
      const button = event.target.closest('[data-amaro-color]');
      if (!button || !root.contains(button) || button.getAttribute('aria-pressed') === 'true') return;
      const card = button.closest('[data-amaro-card]');
      const template = Array.from(card.querySelectorAll('template[data-amaro-color-content]'))
        .find((item) => item.dataset.amaroColorContent === button.dataset.amaroColor);
      const content = card.querySelector('.amaro-card-content');
      if (!template || !content) return;
      content.replaceChildren(template.content.cloneNode(true));
      card.querySelectorAll('[data-amaro-color]').forEach((option) => {
        option.setAttribute('aria-pressed', String(option === button));
      });
      card.querySelector('[data-amaro-color-name]').textContent = button.dataset.colorName;
    }, { signal });
    const observer = 'IntersectionObserver' in window
      ? new IntersectionObserver((entries) => {
          for (const entry of entries) {
            if (!entry.isIntersecting) continue;
            entry.target.classList.add('amaro-visible');
            observer.unobserve(entry.target);
          }
        }, { threshold: 0.08 })
      : null;

    root.querySelectorAll('[data-amaro-reveal]').forEach((element) => {
      if (observer && !reducedMotion.matches) observer.observe(element);
      else element.classList.add('amaro-visible');
    });
    if (observer) cleanups.push(() => observer.disconnect());

    root.querySelectorAll('[data-amaro-rail]').forEach((element) => {
      const track = element.querySelector('[data-amaro-rail-track]');
      const prev = element.querySelector('[data-amaro-prev]');
      const next = element.querySelector('[data-amaro-next]');
      const progress = element.querySelector('[data-amaro-progress]');
      if (!track || !prev || !next) return;
      let railFrame = 0;
      const update = () => {
        railFrame = 0;
        const max = track.scrollWidth - track.clientWidth;
        const offset = Math.abs(track.scrollLeft);
        prev.disabled = offset < 3 || max < 3;
        next.disabled = offset >= max - 3 || max < 3;
        if (progress) progress.value = max > 0 ? Math.min(100, offset / max * 100) : 100;
      };
      const schedule = () => {
        if (!railFrame) railFrame = requestAnimationFrame(update);
      };
      const move = (direction) => {
        const first = track.firstElementChild;
        if (!first) return;
        const style = getComputedStyle(track);
        const gap = parseFloat(style.columnGap) || 0;
        const distance = first.getBoundingClientRect().width + gap;
        const visibleCount = Math.max(1, Math.floor(track.clientWidth / distance));
        const rtl = style.direction === 'rtl' ? -1 : 1;
        track.scrollBy({ left: direction * rtl * distance * visibleCount, behavior: reducedMotion.matches ? 'auto' : 'smooth' });
      };
      prev.addEventListener('click', () => move(-1), { signal });
      next.addEventListener('click', () => move(1), { signal });
      track.addEventListener('scroll', schedule, { passive: true, signal });
      track.addEventListener('keydown', (event) => {
        if (event.target !== track || !['ArrowLeft', 'ArrowRight'].includes(event.key)) return;
        event.preventDefault();
        const rtl = getComputedStyle(track).direction === 'rtl';
        move((event.key === 'ArrowRight' ? 1 : -1) * (rtl ? -1 : 1));
      }, { signal });
      if ('ResizeObserver' in window) {
        const resizeObserver = new ResizeObserver(schedule);
        resizeObserver.observe(track);
        cleanups.push(() => resizeObserver.disconnect());
      } else window.addEventListener('resize', schedule, { signal });
      cleanups.push(() => cancelAnimationFrame(railFrame));
      update();
    });

    root.querySelectorAll('[data-amaro-parallax]').forEach((element) => {
      const media = element.querySelector('[data-amaro-parallax-media]');
      if (!media) return;
      const item = { element, media, visible: true };
      parallaxItems.add(item);
      if ('IntersectionObserver' in window) {
        const visibility = new IntersectionObserver(([entry]) => {
          item.visible = entry.isIntersecting;
          if (item.visible) scheduleParallax();
        }, { rootMargin: '120px' });
        visibility.observe(element);
        cleanups.push(() => visibility.disconnect());
      }
      cleanups.push(() => parallaxItems.delete(item));
    });

    instances.set(root, () => {
      controller.abort();
      cleanups.forEach((cleanup) => cleanup());
      instances.delete(root);
    });
    document.body.classList.add('amaro-motion-ready');
    scheduleParallax();
  };

  const initialize = (scope) => {
    if (scope.matches?.('.amaro-home-section')) mount(scope);
    scope.querySelectorAll('.amaro-home-section').forEach(mount);
  };
  window.addEventListener('scroll', scheduleParallax, { passive: true });
  window.addEventListener('resize', scheduleParallax, { passive: true });
  reducedMotion.addEventListener('change', () => {
    if (reducedMotion.matches) document.querySelectorAll('[data-amaro-reveal]').forEach((element) => element.classList.add('amaro-visible'));
    scheduleParallax();
  });
  document.addEventListener('shopify:section:load', (event) => initialize(event.target));
  document.addEventListener('shopify:section:unload', (event) => instances.get(event.target)?.());
  window.amaroHome = { initialize };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => initialize(document), { once: true });
  else initialize(document);
})();
